import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getRidesFromDateRange, getActiveRides } from "api/rideApi";
import { getPollingDataByRole } from "api/userApi";
import { useApiRequest } from "api/useApiRequest";
import { useAppSelector } from "store/hooks";

const Card = ({ title, value, subtitle }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7", height: "100%" }}
  >
    <Typography variant="body2" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h5" sx={{ mt: 0.5 }}>
      {value}
    </Typography>
    {!!subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "0%");

const TrendBars = ({ daily }) => {
  const max = Math.max(...daily.map((d) => d.total), 1);
  return (
    <Stack gap={1}>
      {daily.map((d) => (
        <Stack key={d.key} direction="row" alignItems="center" gap={1}>
          <Typography variant="caption" sx={{ minWidth: 64 }}>
            {d.label}
          </Typography>
          <Box
            sx={{
              height: 10,
              width: `${Math.max(3, Math.round((d.total / max) * 100))}%`,
              minWidth: 8,
              borderRadius: 1,
              bgcolor: "primary.main",
            }}
          />
          <Typography variant="caption">{d.total}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

export const AnalyticsPage = () => {
  const { callApiRequest } = useApiRequest();
  const navigate = useNavigate();
  const userRole = useAppSelector((s) => s.user?.role);

  const [rides, setRides] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [indicators, setIndicators] = useState(null);

  const [dateRange, setDateRange] = useState("30d");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [driverFilter, setDriverFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadData = () => {
    const now = Date.now();
    const days = dateRange === "7d" ? 7 : dateRange === "90d" ? 90 : 30;
    const dayTo = now;
    const dayFrom = now - days * 24 * 60 * 60 * 1000;

    callApiRequest({
      apiRequest: getRidesFromDateRange,
      params: { dayFrom, dayTo },
      skipSpinner: true,
      onSuccess: (data) => setRides(data || []),
      onError: () => setRides([]),
    });

    callApiRequest({
      apiRequest: getActiveRides,
      params: { pageNum: 0, recordsPerPage: 200 },
      skipSpinner: true,
      onSuccess: (data) => setActiveRides(data?.content || data || []),
      onError: () => setActiveRides([]),
    });

    if (userRole) {
      callApiRequest({
        apiRequest: getPollingDataByRole,
        params: userRole,
        skipSpinner: true,
        skipErrorHandling: true,
        onSuccess: (data) => setIndicators(data || null),
      });
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, dateRange]);

  const facilities = useMemo(
    () => [...new Set(rides.map((r) => r.companyName).filter(Boolean))].sort(),
    [rides]
  );
  const drivers = useMemo(
    () => [...new Set(rides.map((r) => r.driverName).filter(Boolean))].sort(),
    [rides]
  );

  const filteredRides = useMemo(
    () =>
      rides.filter((r) => {
        if (facilityFilter !== "all" && r.companyName !== facilityFilter) return false;
        if (driverFilter !== "all" && r.driverName !== driverFilter) return false;
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        return true;
      }),
    [rides, facilityFilter, driverFilter, statusFilter]
  );

  const stats = useMemo(() => {
    const total = filteredRides.length;
    const byStatus = filteredRides.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});

    const completed = byStatus.COMPLETED || 0;
    const cancelled = byStatus.CANCELLED || 0;
    const assigned =
      (byStatus.CONFIRMATION || 0) +
      (byStatus.SCHEDULED || 0) +
      (byStatus.EN_ROUTE || 0) +
      (byStatus.ARRIVED || 0) +
      (byStatus.ON_BOARD || 0) +
      completed;

    const facilityMap = {};
    const driverMap = {};
    filteredRides.forEach((r) => {
      const f = r.companyName || "Unknown";
      facilityMap[f] = (facilityMap[f] || 0) + 1;
      const d = r.driverName || "Unassigned";
      driverMap[d] = (driverMap[d] || 0) + 1;
    });

    const topFacilities = Object.entries(facilityMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    const topDrivers = Object.entries(driverMap)
      .filter(([name]) => name !== "Unassigned")
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    const dayMap = {};
    filteredRides.forEach((r) => {
      const t = r.plannedStartTime || r.plannedStartTimeMs;
      const d = new Date(t || Date.now());
      const key = d.toISOString().slice(0, 10);
      dayMap[key] = (dayMap[key] || 0) + 1;
    });
    const daily = Object.entries(dayMap)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([key, value]) => ({ key, total: value, label: key.slice(5) }));

    const actionQueue = filteredRides
      .filter((r) => ["UNMATCHED", "NEW", "CONFIRMATION"].includes(r.status))
      .slice(0, 10);

    return {
      total,
      assigned,
      completed,
      cancelled,
      assignmentRate: pct(assigned, total),
      completionRate: pct(completed, total),
      cancellationRate: pct(cancelled, total),
      topFacilities,
      topDrivers,
      byStatus,
      daily,
      actionQueue,
    };
  }, [filteredRides]);

  return (
    <Stack gap={2} p={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Analytics
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Deep performance reporting (SLA, driver, fleet, financial trends)
      </Typography>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select value={dateRange} label="Date Range" onChange={(e) => setDateRange(e.target.value)}>
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Facility</InputLabel>
              <Select value={facilityFilter} label="Facility" onChange={(e) => setFacilityFilter(e.target.value)}>
                <MenuItem value="all">All Facilities</MenuItem>
                {facilities.map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Driver</InputLabel>
              <Select value={driverFilter} label="Driver" onChange={(e) => setDriverFilter(e.target.value)}>
                <MenuItem value="all">All Drivers</MenuItem>
                {drivers.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {["NEW", "UNMATCHED", "CONFIRMATION", "SCHEDULED", "EN_ROUTE", "ARRIVED", "ON_BOARD", "COMPLETED", "CANCELLED"].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1.5}>
            <Button fullWidth variant="outlined" onClick={loadData}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><Card title="Total Trips" value={stats.total} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Active Rides Now" value={activeRides?.length || 0} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Assignment Rate" value={stats.assignmentRate} subtitle={`${stats.assigned}/${stats.total}`} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Completion Rate" value={stats.completionRate} subtitle={`${stats.completed}/${stats.total}`} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Cancellation Rate" value={stats.cancellationRate} subtitle={`${stats.cancelled}/${stats.total}`} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Unmatched Rides" value={indicators?.unmatchedRides?.length || 0} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Drivers Pending Approval" value={indicators?.driversForApproval || 0} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Card title="Vehicles Pending Approval" value={indicators?.vehiclesForApproval || 0} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Trip Trend (last up to 14 days)
            </Typography>
            <TrendBars daily={stats.daily} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Action Queue (needs attention)
            </Typography>
            {stats.actionQueue.map((r) => (
              <Stack key={r.id} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  {r.passengerName || "Unknown"} · {r.companyName || "Unknown"}
                </Typography>
                <Button size="small" onClick={() => navigate(`/ride_chat/${r.id}`)}>
                  {r.status}
                </Button>
              </Stack>
            ))}
            {!stats.actionQueue.length && <Typography variant="body2">No urgent rides.</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Top Facilities by Trip Volume
            </Typography>
            {stats.topFacilities.map(([name, count]) => (
              <Stack key={name} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                <Typography variant="body2">{name}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{count}</Typography>
              </Stack>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Top Drivers by Trip Volume
            </Typography>
            {stats.topDrivers.map(([name, count]) => (
              <Stack key={name} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                <Typography variant="body2">{name}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{count}</Typography>
              </Stack>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Status Mix
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {Object.entries(stats.byStatus).map(([status, count]) => (
            <Typography key={status} variant="body2">
              {status}: <strong>{count}</strong>
            </Typography>
          ))}
        </Box>
      </Paper>
    </Stack>
  );
};
